import { alpha, createTheme, darken, lighten } from "@mui/material/styles";
import "./theme.types.js"; // Import type augmentations
/**
 * The app's design language: bold display type, soft off-white (or ink)
 * canvas, borderless-feeling cards with hairline outlines, generous radii and
 * one vivid brand accent. Everything below derives from `primary`, so a
 * retailer's brand colour re-skins the whole app.
 */
const FONT_STACK = [
    '"Plus Jakarta Sans Variable"',
    '"Plus Jakarta Sans"',
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    '"Noto Sans Tamil"',
    "Roboto",
    "sans-serif",
].join(",");
/** Default accent when the retailer has not set a brand colour. */
export const DEFAULT_PRIMARY = "#5B3DF5";
/** Default strong neutral (dark buttons, selected chips, dark cards). */
export const DEFAULT_SECONDARY = "#0B0B0F";
const ink = "#0B0B0F";
const neutrals = (mode) => mode === "light"
    ? {
        background: { default: "#F4F3EF", paper: "#FFFFFF" },
        text: { primary: ink, secondary: "#5E5E6B", disabled: "#A3A3AD" },
        divider: alpha(ink, 0.08),
        surface: "#ECEBE6",
    }
    : {
        background: { default: "#09090C", paper: "#141419" },
        text: { primary: "#F5F5F7", secondary: "#A1A1AD", disabled: "#5C5C66" },
        divider: alpha("#FFFFFF", 0.09),
        surface: "#1C1C23",
    };
const isHex = (value) => Boolean(value && /^#[0-9a-f]{6}$/i.test(value));
export const createAppTheme = (mode = "light", brand) => {
    const primaryMain = isHex(brand?.primary) ? brand.primary : DEFAULT_PRIMARY;
    // The brand neutral is used as-is in light mode; dark mode keeps a light
    // neutral so dark buttons stay visible on the dark canvas.
    const secondaryMain = isHex(brand?.secondary) ? brand.secondary : ink;
    const n = neutrals(mode);
    const radius = 14;
    const base = createTheme({
        palette: {
            mode,
            primary: {
                main: primaryMain,
                light: lighten(primaryMain, 0.2),
                dark: darken(primaryMain, 0.2),
                lighter: alpha(primaryMain, mode === "light" ? 0.1 : 0.18),
            },
            secondary: {
                main: mode === "light" ? secondaryMain : "#F5F5F7",
                contrastText: mode === "light" ? "#FFFFFF" : ink,
            },
            success: { main: mode === "light" ? "#0E9F6E" : "#34D399", lighter: alpha("#0E9F6E", 0.12) },
            error: { main: mode === "light" ? "#E5484D" : "#FF6B6B", lighter: alpha("#E5484D", 0.12) },
            warning: { main: "#F5A524", contrastText: ink },
            info: { main: "#3B82F6" },
            background: n.background,
            text: n.text,
            divider: n.divider,
            action: {
                hover: alpha(mode === "light" ? ink : "#FFFFFF", 0.05),
                selected: alpha(primaryMain, 0.12),
            },
        },
        shape: { borderRadius: radius },
        spacing: 8,
        typography: {
            fontFamily: FONT_STACK,
            h1: { fontSize: "2.75rem", fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.05 },
            h2: { fontSize: "2.25rem", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 },
            h3: { fontSize: "1.875rem", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15 },
            h4: { fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 },
            h5: { fontSize: "1.25rem", fontWeight: 750, letterSpacing: "-0.015em" },
            h6: { fontSize: "1.0625rem", fontWeight: 700, letterSpacing: "-0.01em" },
            subtitle1: { fontWeight: 650 },
            subtitle2: { fontWeight: 700 },
            button: { fontWeight: 700, letterSpacing: "-0.005em" },
            overline: { fontWeight: 700, letterSpacing: "0.08em" },
        },
    });
    return createTheme(base, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                        fontFeatureSettings: '"ss01", "cv11"',
                    },
                    "::selection": { background: alpha(primaryMain, 0.25) },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        borderRadius: 12,
                        fontWeight: 700,
                        paddingInline: 18,
                        minHeight: 44,
                        transition: "transform 120ms ease, background-color 200ms ease, box-shadow 200ms ease",
                        "&:active": { transform: "scale(0.98)" },
                    },
                    sizeLarge: { minHeight: 54, fontSize: "1rem", borderRadius: 16, paddingInline: 24 },
                    sizeSmall: { minHeight: 34, borderRadius: 10, paddingInline: 12 },
                    containedPrimary: {
                        boxShadow: `0 8px 24px -10px ${alpha(primaryMain, 0.7)}`,
                        "&:hover": { boxShadow: `0 10px 28px -10px ${alpha(primaryMain, 0.85)}` },
                    },
                    outlined: {
                        borderColor: n.divider,
                        borderWidth: 1.5,
                        "&:hover": { borderWidth: 1.5 },
                    },
                    outlinedInherit: { borderColor: n.divider },
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 12 } },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundImage: "none" },
                    rounded: { borderRadius: 20 },
                    outlined: { borderColor: n.divider },
                },
            },
            MuiCard: {
                defaultProps: { variant: "outlined" },
                styleOverrides: { root: { borderRadius: 20, borderColor: n.divider } },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "medium" },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: radius,
                        backgroundColor: mode === "light" ? "#FFFFFF" : n.surface,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: n.divider, borderWidth: 1.5 },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(mode === "light" ? ink : "#FFFFFF", 0.25),
                        },
                    },
                },
            },
            MuiInputLabel: { styleOverrides: { root: { fontWeight: 500 } } },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 10, fontWeight: 650 },
                    outlined: { borderColor: n.divider, borderWidth: 1.5 },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        backgroundColor: n.surface,
                        borderRadius: 14,
                        padding: 4,
                        gap: 4,
                    },
                    grouped: {
                        border: 0,
                        borderRadius: "10px !important",
                        margin: 0,
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 700,
                        color: n.text.secondary,
                        paddingInline: 14,
                        "&.Mui-selected": {
                            backgroundColor: base.palette.background.paper,
                            color: n.text.primary,
                            boxShadow: `0 1px 2px ${alpha(ink, 0.08)}, 0 4px 12px -4px ${alpha(ink, 0.12)}`,
                            "&:hover": { backgroundColor: base.palette.background.paper },
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: { height: 3, borderRadius: 3 },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: { textTransform: "none", fontWeight: 700, fontSize: "0.95rem", minHeight: 48 },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: 24, backgroundImage: "none" },
                },
            },
            MuiDialogTitle: {
                styleOverrides: { root: { fontWeight: 800, fontSize: "1.25rem", letterSpacing: "-0.015em" } },
            },
            MuiAlert: {
                styleOverrides: { root: { borderRadius: 14, fontWeight: 500 } },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 16,
                        border: `1px solid ${n.divider}`,
                        boxShadow: `0 16px 40px -12px ${alpha(ink, 0.25)}`,
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: { root: { borderRadius: 10, marginInline: 6, minHeight: 44 } },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { borderRadius: 8, fontWeight: 600, backgroundColor: ink },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { padding: 8 },
                    track: { borderRadius: 11 },
                },
            },
            MuiLinearProgress: {
                styleOverrides: { root: { borderRadius: 4, height: 6 } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: n.divider },
                    head: {
                        fontWeight: 700,
                        color: n.text.secondary,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                    },
                },
            },
            MuiSkeleton: {
                styleOverrides: { rounded: { borderRadius: 16 } },
            },
        },
    });
};
/**
 * Default light theme (for backwards compatibility)
 */
export const theme = createAppTheme("light");
//# sourceMappingURL=theme.config.js.map